import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LogOut, Save, Settings } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [contactInfo, setContactInfo] = useState({
    address: '',
    city: '',
    phone_primary: '',
    phone_secondary: '',
    email_primary: '',
    email_secondary: '',
    business_hours_weekday: '',
    business_hours_weekend: '',
    facebook_url: '',
    twitter_url: '',
    linkedin_url: '',
    instagram_url: '',
  });

  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    tagline: '',
    hero_title: '',
    hero_subtitle: '',
    about_description: '',
  });

  const [stats, setStats] = useState({
    stats: [
      { number: '', label: '' },
      { number: '', label: '' },
      { number: '', label: '' },
      { number: '', label: '' },
    ]
  });

  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    verifyToken();
    fetchAllData();
  }, []);

  const verifyToken = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.post(`${API}/admin/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      toast({
        title: "Session Expired",
        description: "Please login again",
        variant: "destructive",
      });
      localStorage.removeItem('admin_token');
      localStorage.removeItem('token_expires');
      navigate('/admin/login');
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [contactRes, companyRes, statsRes] = await Promise.all([
        axios.get(`${API}/contact/contact-info`),
        axios.get(`${API}/content/company-info`),
        axios.get(`${API}/content/stats`)
      ]);
      
      setContactInfo(contactRes.data);
      setCompanyInfo(companyRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContactChange = (e) => {
    setContactInfo({
      ...contactInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleCompanyChange = (e) => {
    setCompanyInfo({
      ...companyInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleStatChange = (index, field, value) => {
    const newStats = [...stats.stats];
    newStats[index][field] = value;
    setStats({ stats: newStats });
  };

  const saveContactInfo = async () => {
    setSaving(true);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/contact/contact-info`, contactInfo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "Contact information updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const saveCompanyInfo = async () => {
    setSaving(true);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/content/company-info`, companyInfo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "Company information updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const saveStats = async () => {
    setSaving(true);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/content/stats`, stats, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "Stats updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Ignore errors
    } finally {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('token_expires');
      navigate('/admin/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Settings className="text-emerald-600" size={24} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
                <p className="text-sm text-gray-600">International Quality Group</p>
              </div>
            </div>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="contact" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="contact">Contact Info</TabsTrigger>
            <TabsTrigger value="company">Company Info</TabsTrigger>
            <TabsTrigger value="stats">Stats</TabsTrigger>
          </TabsList>

          {/* Contact Info Tab */}
          <TabsContent value="contact">
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
                <p className="text-gray-600 mt-2">Manage contact details, business hours, and social media links</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Address */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Address</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="address">Street Address</Label>
                      <Input id="address" name="address" value={contactInfo.address} onChange={handleContactChange} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">City & State</Label>
                      <Input id="city" name="city" value={contactInfo.city} onChange={handleContactChange} />
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Phone Numbers</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone_primary">Primary Phone</Label>
                      <Input id="phone_primary" name="phone_primary" value={contactInfo.phone_primary} onChange={handleContactChange} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone_secondary">Secondary Phone</Label>
                      <Input id="phone_secondary" name="phone_secondary" value={contactInfo.phone_secondary} onChange={handleContactChange} />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Email Addresses</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email_primary">Primary Email</Label>
                      <Input id="email_primary" name="email_primary" type="email" value={contactInfo.email_primary} onChange={handleContactChange} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email_secondary">Secondary Email</Label>
                      <Input id="email_secondary" name="email_secondary" type="email" value={contactInfo.email_secondary} onChange={handleContactChange} />
                    </div>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Business Hours</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="business_hours_weekday">Weekday Hours</Label>
                      <Input id="business_hours_weekday" name="business_hours_weekday" value={contactInfo.business_hours_weekday} onChange={handleContactChange} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="business_hours_weekend">Weekend Hours</Label>
                      <Input id="business_hours_weekend" name="business_hours_weekend" value={contactInfo.business_hours_weekend} onChange={handleContactChange} />
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Social Media Links</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="facebook_url">Facebook URL</Label>
                      <Input id="facebook_url" name="facebook_url" value={contactInfo.facebook_url} onChange={handleContactChange} placeholder="https://facebook.com/yourpage" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="twitter_url">Twitter URL</Label>
                      <Input id="twitter_url" name="twitter_url" value={contactInfo.twitter_url} onChange={handleContactChange} placeholder="https://twitter.com/yourhandle" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="linkedin_url">LinkedIn URL</Label>
                      <Input id="linkedin_url" name="linkedin_url" value={contactInfo.linkedin_url} onChange={handleContactChange} placeholder="https://linkedin.com/company/yourcompany" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="instagram_url">Instagram URL</Label>
                      <Input id="instagram_url" name="instagram_url" value={contactInfo.instagram_url} onChange={handleContactChange} placeholder="https://instagram.com/yourhandle" />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={saveContactInfo} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" disabled={saving}>
                    {saving ? 'Saving...' : (<><Save size={18} className="mr-2" />Save Changes</>)}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Company Info Tab */}
          <TabsContent value="company">
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
                <p className="text-gray-600 mt-2">Manage company name, tagline, and descriptions</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input id="company_name" name="company_name" value={companyInfo.company_name} onChange={handleCompanyChange} placeholder="International Quality Group" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tagline">Tagline</Label>
                    <Input id="tagline" name="tagline" value={companyInfo.tagline} onChange={handleCompanyChange} placeholder="Global Logistics Solutions" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hero_title">Hero Title (Home Page)</Label>
                    <Input id="hero_title" name="hero_title" value={companyInfo.hero_title} onChange={handleCompanyChange} placeholder="Delivering Your World, On Time" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hero_subtitle">Hero Subtitle (Home Page)</Label>
                    <Textarea id="hero_subtitle" name="hero_subtitle" value={companyInfo.hero_subtitle} onChange={handleCompanyChange} rows={3} placeholder="Expert international shipping and last-mile delivery solutions..." />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="about_description">About Description</Label>
                    <Textarea id="about_description" name="about_description" value={companyInfo.about_description} onChange={handleCompanyChange} rows={4} placeholder="With over a decade of experience..." />
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={saveCompanyInfo} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" disabled={saving}>
                    {saving ? 'Saving...' : (<><Save size={18} className="mr-2" />Save Changes</>)}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <Card>
              <CardHeader>
                <CardTitle>Company Stats</CardTitle>
                <p className="text-gray-600 mt-2">Update the statistics displayed on your website</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {stats.stats.map((stat, index) => (
                    <div key={index} className="space-y-4 p-4 border rounded-lg">
                      <h3 className="font-semibold text-gray-900">Stat {index + 1}</h3>
                      <div className="space-y-2">
                        <Label>Number/Value</Label>
                        <Input 
                          value={stat.number} 
                          onChange={(e) => handleStatChange(index, 'number', e.target.value)}
                          placeholder="50+"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Label</Label>
                        <Input 
                          value={stat.label} 
                          onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                          placeholder="Countries Served"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={saveStats} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" disabled={saving}>
                    {saving ? 'Saving...' : (<><Save size={18} className="mr-2" />Save Changes</>)}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;